import { useEffect, useState } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { VERIFY_EVENT } from "@/components/invoices/VerifyBox";
import { Home, ScrollText, Receipt, MoreHorizontal, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import AiChatDrawer from "@/components/AiChatDrawer";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

export default function AppLayout() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const wide = pathname.startsWith("/app/factures");
  const [verifyCount, setVerifyCount] = useState(0);
  useEffect(() => {
    const load = () => api.get("/invoices/to-verify").then(({ data }) => setVerifyCount(data.count)).catch(() => {});
    load();
    window.addEventListener(VERIFY_EVENT, load);
    return () => window.removeEventListener(VERIFY_EVENT, load);
  }, [pathname]);

  const items = [
    { to: "/app/accueil", label: t("dashboard_nav.home"), icon: Home, testid: "nav-accueil" },
    { to: "/app/caisse", label: t("dashboard_nav.cash"), icon: ScrollText, testid: "nav-caisse" },
    { to: "/app/factures", label: t("dashboard_nav.invoices"), icon: Receipt, testid: "nav-factures" },
    { to: "/app/plus", label: t("dashboard_nav.more"), icon: MoreHorizontal, testid: "nav-plus" },
  ];

  const trialDays = (() => {
    if (!user?.trial_end || user?.subscription_status === "active") return null;
    const ms = new Date(user.trial_end).getTime() - Date.now();
    return Math.ceil(ms / (1000 * 60 * 60 * 24));
  })();
  const showTrialWarning = trialDays !== null && trialDays >= 0 && !user?.lifetime_access;
  const trialUrgent = trialDays !== null && trialDays <= 2;
  const trialExpired = user?.subscription_status !== "active" && trialDays !== null && trialDays < 0;

  return (
    <div className="min-h-screen bg-bg">
      {/* Desktop side gradient — subtle depth without overwhelming */}
      <div className="hidden lg:block fixed inset-0 pointer-events-none" aria-hidden="true"
        style={{ background: "radial-gradient(ellipse at top left, rgba(42,125,95,0.06), transparent 50%), radial-gradient(ellipse at bottom right, rgba(200,160,64,0.05), transparent 55%)" }} />

      <div
        className={`relative ${wide ? "max-w-md md:max-w-4xl" : "max-w-md"} mx-auto pb-32 px-4 sm:px-6 page-enter`}
        style={{ paddingTop: "max(1rem, calc(env(safe-area-inset-top) + 0.5rem))" }}
      >
        <header className="flex items-center justify-between mb-4">
          <div className="min-w-0 flex-1 pr-3">
            <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold">{user?.establishment?.canton || ""}</p>
            <h2 className="font-display text-2xl leading-tight truncate">{user?.establishment?.name || t("dashboard.my_restaurant")}</h2>
          </div>
          {user?.establishment?.logo_data_url ? (
            <img src={user.establishment.logo_data_url} alt="Logo" className="w-12 h-12 rounded-2xl object-cover border border-c shrink-0" />
          ) : (
            <div className="w-12 h-12 rounded-2xl bg-primary-c flex items-center justify-center text-white font-display text-lg shrink-0">
              {(user?.establishment?.name || "O")[0]}
            </div>
          )}
          <div className="ml-1 shrink-0 flex items-center gap-1">
            <ThemeToggle compact />
            <LanguageSwitcher compact />
          </div>
        </header>

        {trialExpired && (
          <div className="mb-4 rounded-2xl border-2 border-red-300 bg-red-50 p-3 flex items-center gap-3" data-testid="trial-expired-banner">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-700">{t("dashboard.trial_expired")}</p>
              <p className="text-xs text-red-600">{t("dashboard.trial_expired_msg")}</p>
            </div>
          </div>
        )}
        {!trialExpired && showTrialWarning && (
          <div className={`mb-4 rounded-2xl border p-3 flex items-center gap-3 ${trialUrgent ? "border-[#F5C87A] bg-[#FFF8EB]" : "border-c bg-surface-muted"}`} data-testid="trial-banner">
            <AlertCircle className={`w-5 h-5 shrink-0 ${trialUrgent ? "text-[#B45309]" : "text-primary-c"}`} />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${trialUrgent ? "text-[#B45309]" : ""}`} data-testid="trial-banner-days">
                {trialDays === 0 ? t("dashboard.trial_last_day") : t("dashboard.trial_banner", { count: trialDays })}
              </p>
              <p className="text-xs text-text-secondary">{t("dashboard.trial_warning_msg")}</p>
            </div>
            <Link to="/app/plus" data-testid="trial-banner-subscribe" className="btn-primary !py-2 !px-3 text-xs shrink-0">{t("dashboard.subscribe")}</Link>
          </div>
        )}

        <Outlet />
      </div>

      <nav data-testid="bottom-nav" className={`fixed bottom-0 inset-x-0 ${wide ? "max-w-md md:max-w-4xl md:rounded-t-2xl" : "max-w-md"} mx-auto bg-surface border-t border-c flex justify-around py-2 z-50 shadow-[0_-4px_12px_-4px_rgba(0,0,0,0.06)]`}
        style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}>
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            data-testid={`${it.testid}-tab`}
            className={({ isActive }) =>
              `relative flex flex-col items-center gap-1 px-4 py-1.5 rounded-xl transition-colors ${isActive ? "text-primary-c" : "text-text-secondary"}`
            }
          >
            {({ isActive }) => (
              <>
                {it.testid === "nav-factures" && verifyCount > 0 && (
                  <span className="absolute top-0 right-2 min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center" data-testid="nav-factures-badge" aria-label={t("verify.title")}>{verifyCount}</span>
                )}
                <it.icon className="w-5 h-5" strokeWidth={isActive ? 2.4 : 1.7} fill={isActive ? "currentColor" : "none"} fillOpacity={isActive ? 0.12 : 0} />
                <span className="text-[11px] font-semibold">{it.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <AiChatDrawer />
      <PWAInstallPrompt />
    </div>
  );
}
